import DOMPurify from 'dompurify'

export const INBOX_SURFACE = 'CMS'
export const INBOX_CATEGORY = 'TASK_COMPLETE'
export const INBOX_PAGE_SIZE = 10
export const INBOX_POLL_INTERVAL_MS = 60 * 1000

export interface InboxMessage {
  id: string
  surface: string
  category: string
  title: string
  htmlContent: string
  plainContent?: string
  ctaLabel?: string
  ctaUrl?: string
  publishedAt: string
  readAt: string | null
  clickedAt: string | null
}

export interface InboxResponse {
  data: InboxMessage[]
  total: number
  unreadCount: number
  newCount: number
  limit: number
  offset: number
}

interface AuthInput {
  apiUrl: string
  token: string
}

function authHeaders (token: string) {
  return { Authorization: `Bearer ${token}` }
}

export function buildInboxRequest ({ apiUrl, token }: AuthInput) {
  return {
    url: `${apiUrl}/v1/messaging/inbox`,
    headers: authHeaders(token),
    params: {
      surface: INBOX_SURFACE,
      category: INBOX_CATEGORY,
      limit: INBOX_PAGE_SIZE,
      offset: 0
    }
  }
}

export function buildSeenRequest ({ apiUrl, token }: AuthInput) {
  return {
    url: `${apiUrl}/v1/messaging/inbox/seen`,
    body: { surface: INBOX_SURFACE },
    headers: authHeaders(token)
  }
}

export function buildReadAllRequest ({ apiUrl, token }: AuthInput) {
  return {
    url: `${apiUrl}/v1/messaging/inbox/read-all`,
    body: { surface: INBOX_SURFACE },
    headers: authHeaders(token)
  }
}

export function buildMessageActionRequest ({ apiUrl, token, id, action }: AuthInput & { id: string, action: 'read' | 'click' }) {
  return {
    url: `${apiUrl}/v1/messaging/inbox/${encodeURIComponent(id)}/${action}`,
    headers: authHeaders(token)
  }
}

// Unread task rows among those we hold; the inbox's unreadCount covers the
// whole CMS surface, not just the category we filter on.
export function countUnread (messages: InboxMessage[]): number {
  return messages.filter(message => message.readAt === null).length
}

export function markMessagesRead (messages: InboxMessage[], ids: string[] | 'all', readAt: string): InboxMessage[] {
  return messages.map(message => {
    if (message.readAt !== null) return message
    if (ids !== 'all' && !ids.includes(message.id)) return message
    return { ...message, readAt }
  })
}

export function getCategoryLabel (category: string): string {
  switch (category) {
    case 'TASK_COMPLETE':
      return 'Task complete'
    default:
      return 'Notification'
  }
}

export function sanitizeNotificationHtml (html?: string): string {
  if (!html) return ''
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } })
}
